import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { confirmAlert } from "react-confirm-alert";
import toast from "react-hot-toast";

import { backendApi, useCreateLogMutation, useCreateServiceMutation, useDeleteServiceMutation, useGetAllServicesQuery } from "../../app/services/backendApi";

import { DataGrid } from '@mui/x-data-grid';
import Button from '@mui/material/Button';

import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { authSelector } from "../auth/authSlice";

import { ENV } from "../../common/utils/apiConstants";

import styles from "../../styles/servicePanel.module.css"


/** ------------------------------------------------------------------------ */
const ServicePanel = () => {

  const auth = useSelector(authSelector);

  // - handling for log notification
  let errorInfos = {
    environment: ENV,
    userEmail: auth.user.email,
    code: 400,
    criticite: 3
  }
  const [ triggerLog ] = useCreateLogMutation();

  const [ triggerDeleteService ] = useDeleteServiceMutation();

  const { data,isLoading } = useGetAllServicesQuery();
  const [triggerCreateService] = useCreateServiceMutation();
  const [triggerRefreshServices, refreshServicesList] = backendApi.endpoints.getAllServices.useLazyQuery();
  const [triggerGetTokenSeed, tokenSeed] = backendApi.endpoints.getGeneratedToken.useLazyQuery();

  const [ rows, setRows ] = useState([]);

  useEffect(() => {
    if (refreshServicesList.isSuccess) {
      setRows(refreshServicesList.data)
    }
  },[refreshServicesList.data]) 

  useEffect(() => {
    if (! isLoading) {
      setRows(data);
    }
  }, [data])

  const { register, setValue, handleSubmit, formState: { errors } } = useForm();
  
  const refreshServices = () => {
    triggerRefreshServices() 
  }

  const generateToken = () => {
    triggerGetTokenSeed()
  }

  useEffect(() => {
    if (tokenSeed.isSuccess) {
      setValue("serviceToken", tokenSeed.data, {
        shouldValidate: true,
        shouldDirty: true
      })
    }
  }, [tokenSeed.data])

  /** - Handling submit form add service */
  const onSubmit = async (data) => {
    
    let formParams = {
      name: data.serviceName,
      surname: data.serviceSurname,
      token: data.serviceToken
    };

    await triggerCreateService(formParams)
      .unwrap()
      .then(() => {
        toast.success(
          "Nouveau service enregistré avec succès."
        );
        refreshServices()
      })
      .catch((error) => {
        toast.error(
          <>
            <div>😝😆 Une erreur s'est produite. Le service n'a pas pu être enregistré. 😆😝<br /><br />
            Détails: {error.data.message}</div>
          </>,
          {
            duration: 5000,
            className: `${styles.lgError}`,
          } 
        )

        errorInfos.className = 'ServicePanel.jsx::createService()'
        errorInfos.message =  error.data.message
        triggerLog(errorInfos);
      });
  }

  /**
   * 
   * @param {*} message 
   * @param {*} orderId 
   */
  function confirmPopup(serviceId)  {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <div className={styles.customUI}>
            <div className={styles.beatsWarning}>
              <WarningAmberIcon className={styles.warning}/>
            </div>
            <span className={styles.titleWarning}>Confirmer</span>
            
            <p>Êtes-vous sûr de vouloir <span className={styles.bold}>definitevement supprimer</span> ce service ?</p>
            <button onClick={onClose}>Refuser</button>
            <button onClick={ async() => {
              await triggerDeleteService(serviceId)
                .unwrap()
                .then(() => {
                  toast.success("Le service a été supprimé")
                  refreshServices()
                })
                .catch((error) => {
                  toast.error("Une erreur s'est produite lors de la suppression du service : ", error.data.message)

                  errorInfos.className = 'ServicePanel.jsx::deleteService()'
                  errorInfos.message =  error.data.message
                  triggerLog(errorInfos);
                });
              onClose();
            }}>Valider</button> 
          </div>
        );
      }
    });
  
}

  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      headerClassName: `${styles.tableHeader}`,
      width: 50,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
       },
    {
      field: 'name',
      headerName: 'service',
      headerClassName: `${styles.tableHeader}`,
      flex: 1,
      minWidth: 300,
      editable: false,
    },

    {
      field: 'api_token',
      headerName: 'token',
      headerClassName: `${styles.tableHeader}`,
      flex: 1,
      minWidth: 400,
      editable: false,
    },
    
    {
      field: 'actions',
      headerName: ' .',
      headerClassName: `${styles.tableHeader}`,
      flex: 1,
      minWidth: 48,
      maxWidth: 48,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,

      align: "center",
      renderCell: (params) => (
        <DeleteForeverIcon
          className={styles.see}
          onClick={() => { confirmPopup(params.id) }}
        />
      ),
    },
    
  ];

  return (
    <>
      <div className={styles.mb20}>
        <span className={styles.titleH5}>Liste des services</span>
        <Button variant="outlined" className={styles.refreshPosition} onClick={() => refreshServices()}>Rafraichir</Button>
        <div className={styles.listServices}>
          <DataGrid
            rows={rows}
            columns={columns}
            pageSize={5}
            rowsPerPageOptions={[5]}
            disableSelectionOnClick
          />
        </div>
      </div>

      <span className={styles.titleH5}>Ajouter un service</span>
      <div className={styles.formContent}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <label>Nom du service:</label><br/>
          <input placeholder="*nom" type="text" {...register("serviceName", { required: true, maxLength: 32 })} /><br />
          {/* errors will return when field validation fails  */}
          {errors.serviceName && <span className={styles.formError}>Champs obligatoire avec une taille max. de 32 caractères.</span>}
          <br/><br/>

          <label>Surnom:</label><br/>
          <input placeholder="*surnom" type="text" {...register("serviceSurname", { required: false, maxLength: 32 })} /><br />
          {/* errors will return when field validation fails  */}
          {errors.serviceSurname && <span className={styles.formError}>Champs obligatoire avec une taille max. de 32 caractères.</span>}
          <br/><br/>

          <label>API Token:</label><br />
          
          {/* include validation with required or other standard HTML validation rules */}
          <input placeholder="*token" type="text" {...register("serviceToken", { required: true, maxLength: 32 })} className={styles.inputToken} />
          <Button variant="outlined" className={styles.btnToken} onClick={() => generateToken()}>Génerer</Button>
          <br />
          {/* errors will return when field validation fails  */}
          {errors.serviceToken && <span className={styles.formError}>Champs obligatoire avec une taille max. de 32 caractères.</span>}
          
          
          <br /><br />
          
          <input type="submit" value="Ajouter" />
        </form>
      </div>

    </>
  );
}

export default ServicePanel;